import { useMutation } from '@apollo/client';
import { useEffect } from 'react';

import { useLocation } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import type { JobPostingSkillFragment } from '@xing-com/crate-jobs-graphql';
import { RegisterVisitDocument } from '@xing-com/crate-jobs-graphql';
import type { JobDetails, Preference } from '@xing-com/crate-jobs-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';

type Props = {
  job: JobDetails;
  matchedJobPreferences?: Preference[]; // used for nwt tracking
  matchedSkills?: JobPostingSkillFragment[];
  skip: boolean;
};

export const useJobDetailsTracking = ({
  job,
  matchedJobPreferences,
  matchedSkills,
  skip,
}: Props): void => {
  const { isLoggedIn } = useLoginState();
  const location = useLocation();
  const trackNwt = useJobsNwtTracking();
  const [registerVisitMutation] = useMutation(RegisterVisitDocument);

  const nwtFlags = isLoggedIn
    ? `matching_preferences_${matchedJobPreferences ? matchedJobPreferences.length : 0},matching_skills_${matchedSkills ? matchedSkills.length : 0}`
    : undefined;

  useEffect(() => {
    if (!skip) {
      const page = `jobs/detail${job.redirectsToThirdPartyUrl ? '_external' : ''}`;
      const context = job.redirectsToThirdPartyUrl ? 'no_redirect' : undefined;
      trackNwt({
        eventSchema: 'basic',
        event: 'viewed_screen',
        page,
        itemUrn: `surn:x-xing:jobs:posting:${job.id}`,
        flags: nwtFlags,
        context,
      });

      trackNwt({
        eventSchema: 'basic',
        event: 'job_detail_viewed',
        page,
        itemUrn: `surn:x-xing:jobs:posting:${job.id}`,
        flags: nwtFlags,
        context,
      });

      registerVisitMutation({
        variables: {
          jobId: job.id,
          originalUrl: location.href,
          referrer: document.referrer,
        },
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [job.id]);
};
